import { ComponentType, Icon, Loader, TableRowExtras, Tooltip } from '@gcl/react-core';
import { ApiSuccessToast } from '@core/ApiSuccessToast';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { useProjectId } from '@config/config.hooks';
import { useQueryClient } from '@tanstack/react-query';
import { FETCH_QUERIES_NAME } from '@common/constants';
import { useConfirmationModal } from '@common/confirmation.hooks';
import { useDeleteProjectMasterScheduleTag, useUpdateProjectMasterScheduleTag } from './masterScheduleTag.queries';
import {
  ETagManageFormKey,
  ITagManageFormState,
  IUpdateProjectMasterScheduleTagRequest,
} from './masterScheduleTag.models';

interface ITagManageRowStateButton {
  rowExtras: TableRowExtras;
}

export const TagManageRowStateButton = ({ rowExtras }: ITagManageRowStateButton) => {
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  const { rowState, setRowState, useFormMethods } = rowExtras;

  const {
    mutate: mutateDeleteProjectMasterScheduleTag,
    isLoading: isLoadingDeleteProjectMasterScheduleTag,
    error: errorDeleteProjectMasterScheduleTag,
    isError: isErrorDeleteProjectMasterScheduleTag,
    isSuccess: isSuccessDeleteProjectMasterScheduleTag,
    reset: resetDeleteProjectMasterScheduleTag,
  } = useDeleteProjectMasterScheduleTag();
  const {
    mutate: mutateUpdateProjectMasterScheduleTag,
    isLoading: isLoadingUpdateProjectMasterScheduleTag,
    error: errorUpdateProjectMasterScheduleTag,
    isSuccess: isSuccessUpdateProjectMasterScheduleTag,
    reset: resetUpdateProjectMasterScheduleTag,
  } = useUpdateProjectMasterScheduleTag();

  const { renderModal: renderDeleteCompanyConfirmation, openPortal: openDeleteCompanyConfirmation } =
    useConfirmationModal({
      content: `Confirm to Delete tag ${
        (useFormMethods?.getValues() as ITagManageFormState | undefined)?.[ETagManageFormKey.ORIGINAL_NAME] ?? ''
      }?`,
      onConfirm: () => {
        onClickDelete();
      },
    });

  const handleSubmit = (data: any) => {
    setRowState?.('Loading');
    mutateUpdateProjectMasterScheduleTag({ ...data, projectId } as IUpdateProjectMasterScheduleTagRequest);
  };
  const onClickCancel = () => {
    useFormMethods?.reset();
    setRowState?.('Idle');
  };
  const onClickDelete = () => {
    const data = useFormMethods?.getValues() as ITagManageFormState | undefined;
    setRowState?.('Loading');

    mutateDeleteProjectMasterScheduleTag({
      projectId,
      name: data?.[ETagManageFormKey.ORIGINAL_NAME] ?? '',
      tagTypeId: data?.[ETagManageFormKey.TAG_TYPE_ID] ?? 0,
    });
  };

  return (
    <>
      <ApiSuccessToast
        successes={[
          {
            isSuccess: isSuccessDeleteProjectMasterScheduleTag,
            message: 'Delete Tag Success',
            reset: resetDeleteProjectMasterScheduleTag,
          },
          {
            isSuccess: isSuccessUpdateProjectMasterScheduleTag,
            message: 'Update Tag Success',
            reset: resetUpdateProjectMasterScheduleTag,
          },
        ]}
        callbackAfterToasted={() => {
          setRowState?.('Idle');
          queryClient.invalidateQueries([projectId, FETCH_QUERIES_NAME.MASTER_SCHEDULE_TAGS_SEARCH, undefined]);
        }}
      />
      <ApiErrorDisplay
        errors={[
          {
            error: errorDeleteProjectMasterScheduleTag,
            genericError: 'Delete Tag Fail',
            reset: resetDeleteProjectMasterScheduleTag,
          },
          {
            error: errorUpdateProjectMasterScheduleTag,
            genericError: 'Update Tag Fail',
            reset: resetUpdateProjectMasterScheduleTag,
          },
        ]}
      />
      {rowState === 'Loading' && <Loader small type={ComponentType.Primary} text="" />}
      {rowState === 'Editing' && (
        <>
          <Tooltip overlay="Save" id="Save">
            <Icon
              iconName="CheckCircle"
              className="gcl-ic-btn-lg gcl-fc-success-400 gcl-mr-xs"
              onClick={() => {
                useFormMethods?.handleSubmit(handleSubmit)();
              }}
            />
          </Tooltip>
          <Tooltip overlay="Cancel" id="cancel" key="cancel">
            <Icon iconName="XCircle" className="gcl-ic-btn-lg gcl-fc-danger-400" onClick={onClickCancel} />
          </Tooltip>
        </>
      )}
      {rowState === 'Idle' && (
        <>
          <Tooltip overlay="Edit" id="edit" key="edit" placement="left">
            <Icon
              iconName="Pencil"
              className="gcl-ic-btn-lg gcl-fc-primary-400 gcl-mr-xs"
              onClick={() => setRowState?.('Editing')}
            />
          </Tooltip>
          <Tooltip overlay="Delete" id="Delete">
            <Icon
              iconName="Trash"
              className="gcl-ic-btn-lg gcl-fc-danger-400"
              onClick={openDeleteCompanyConfirmation}
            />
          </Tooltip>
        </>
      )}
      {renderDeleteCompanyConfirmation(
        isLoadingDeleteProjectMasterScheduleTag,
        isErrorDeleteProjectMasterScheduleTag,
        isSuccessDeleteProjectMasterScheduleTag,
      )}
    </>
  );
};
